import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";

const IOS_APP_URL =
  "https://apps.apple.com/tr/app/kolayy%C3%BCk/id6758580078?l=tr";

const ANDROID_APP_URL =
  "https://play.google.com/store/apps/details?id=com.kolayyuk.app";

const FALLBACK_WEB_URL = "https://www.kolayyuk.com/";

const APPLE_APP_ID = "6758580078";

const OG_IMAGE_URL = "https://cdn.kolayyuk.com/og/kolayyuk-indir.png";

const LOGO_URL = "https://cdn.kolayyuk.com/logo.png";

function detectDevice() {
  if (typeof window === "undefined") return "desktop";

  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const platform = navigator.platform || "";

  const isIPadOS =
    platform === "MacIntel" &&
    typeof navigator !== "undefined" &&
    navigator.maxTouchPoints > 1;

  const isIOS = /iPhone|iPad|iPod/i.test(ua) || isIPadOS;
  const isAndroid = /Android/i.test(ua);

  if (isIOS) return "ios";
  if (isAndroid) return "android";

  return "desktop";
}

function getStoreUrl(device) {
  if (device === "ios") return IOS_APP_URL;
  if (device === "android") return ANDROID_APP_URL;

  return FALLBACK_WEB_URL;
}

export default function IndirPage() {
  const [device, setDevice] = useState("desktop");
  const [countdown, setCountdown] = useState(1);
  const [autoRedirectEnabled, setAutoRedirectEnabled] = useState(true);

  const hasRedirectedRef = useRef(false);

  const targetUrl = useMemo(() => getStoreUrl(device), [device]);

  useEffect(() => {
    const detected = detectDevice();
    setDevice(detected);

    const params = new URLSearchParams(window.location.search);
    const noRedirect = params.get("noRedirect");

    if (noRedirect === "1") {
      setAutoRedirectEnabled(false);
      setCountdown(0);
    }
  }, []);

  useEffect(() => {
    if (!autoRedirectEnabled) return;
    if (device === "desktop") return;
    if (hasRedirectedRef.current) return;

    if (countdown <= 0) {
      hasRedirectedRef.current = true;
      window.location.replace(targetUrl);
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 800);

    return () => clearTimeout(timer);
  }, [autoRedirectEnabled, countdown, device, targetUrl]);

  const statusText =
    device === "ios"
      ? "App Store’a yönlendiriliyorsunuz..."
      : device === "android"
      ? "Google Play’e yönlendiriliyorsunuz..."
      : "Uygulamayı indir";

  return (
    <>
      <Helmet>
        <title>KolayYük Uygulamasını İndir</title>

        <meta
          name="description"
          content="KolayYük uygulamasını cihazına uygun mağazadan indir."
        />

        <meta name="apple-itunes-app" content={`app-id=${APPLE_APP_ID}`} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="KolayYük – Uygulamayı İndir" />
        <meta
          property="og:description"
          content="KolayYük uygulamasını cihazına uygun mağazadan indir."
        />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://kolayyuk.com/indir" />
        <meta property="og:site_name" content="KolayYük" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="KolayYük – Uygulamayı İndir" />
        <meta
          name="twitter:description"
          content="KolayYük uygulamasını cihazına uygun mağazadan indir."
        />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm rounded-3xl border border-gray-200 bg-white p-8 text-center shadow-lg">
          <img src={LOGO_URL} alt="KolayYük" className="mx-auto h-14 w-auto" />

          <p className="mt-4 text-sm text-gray-500">{statusText}</p>

          {device !== "desktop" && autoRedirectEnabled && (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
              <span className="h-2.5 w-2.5 rounded-full bg-black animate-pulse" />
              <span>Yönlendiriliyor</span>
            </div>
          )}

          {(device === "desktop" || !autoRedirectEnabled) && (
            <div className="mt-8 space-y-3">
              <a
                href={IOS_APP_URL}
                className="block w-full rounded-2xl bg-black px-5 py-3.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                App Store
              </a>

              <a
                href={ANDROID_APP_URL}
                className="block w-full rounded-2xl border border-gray-300 px-5 py-3.5 text-sm font-semibold text-black transition hover:bg-gray-50"
              >
                Google Play
              </a>
            </div>
          )}

          {device !== "desktop" && autoRedirectEnabled && (
            <button
              onClick={() => setAutoRedirectEnabled(false)}
              className="mt-6 text-xs text-gray-400 transition hover:text-gray-600"
            >
              Yönlendirme olmazsa dokunun
            </button>
          )}
        </div>
      </div>
    </>
  );
}